import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Gift, Copy, Check, Loader2, ShieldCheck, ArrowRight, KeyRound, Share2, Hammer, Zap } from 'lucide-react';
import ModeSwitcher from '@/components/ModeSwitcher';

export default function AccountPage() {
  const { user, setAdvanced } = useAuth();
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [advBusy, setAdvBusy] = useState(false);
  const [advTutorial, setAdvTutorial] = useState(false);

  const toggleAdvanced = async () => {
    setAdvBusy(true);
    try {
      const next = !user?.is_advanced;
      await setAdvanced(next);
      // Show tour only the first time a user turns advanced ON.
      if (next && !localStorage.getItem('diy_advanced_tour_seen')) {
        setAdvTutorial(true);
        localStorage.setItem('diy_advanced_tour_seen', '1');
      }
    } catch (e) { console.error('Toggle advanced failed:', e); }
    finally { setAdvBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/referral/info'); setRef(r.data); }
      catch (e) { console.warn('Referral info fetch failed:', e); }
    })();
  }, []);

  const shareUrl = ref ? `${window.location.origin}/auth?mode=signup&ref=${ref.referral_code}` : '';
  const shareText = "I've been using DIY X-treme to fix stuff around the house — sign up with my link and we both get 30 days of Pro free.";
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn('Clipboard write failed:', e);
    }
  };

  const share = async () => {
    if (!canNativeShare || !shareUrl) return;
    try {
      await navigator.share({
        title: 'DIY X-treme',
        text: shareText,
        url: shareUrl,
      });
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (e) {
      // User cancelled the share sheet — not an error
      if (e?.name !== 'AbortError') console.warn('Share failed:', e);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr(''); setPwMsg('');
    if (pwNew.length < 6) { setPwErr('New password must be at least 6 characters'); return; }
    setPwBusy(true);
    try {
      await api.post('/auth/me/password', { current_password: pwCurrent, new_password: pwNew });
      setPwMsg('Password updated ✓'); setPwCurrent(''); setPwNew('');
    } catch (e) {
      setPwErr(e.response?.data?.detail || 'Could not update password');
    } finally { setPwBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
      <div className="fade-up">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-2">/account</div>
        <h1 className="font-display text-5xl text-zinc-100 tracking-tight leading-none">
          YOUR <span className="text-gradient-hot">ACCOUNT</span>
        </h1>
        <p className="text-zinc-400 mt-3">{user?.email}</p>
      </div>

      {/* Referral card */}
      <div data-testid="refer-card" className="industrial-card border-t-4 border-t-lime-400 glow-lime">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={20} className="text-lime-400" />
          <div className="font-mono text-xs uppercase tracking-widest text-lime-400">Refer a Friend</div>
        </div>
        <h2 className="font-display text-3xl text-zinc-100 tracking-tight">GIVE 30 · GET 30</h2>
        <p className="text-zinc-400 text-sm mt-1 mb-4">
          Share your link. When someone signs up, you both get 30 days of Pro free.
        </p>
        {ref && (
          <>
            <div className="flex items-center gap-2 border border-zinc-800 bg-zinc-950 p-2 mb-2">
              <div className="font-mono text-xs text-zinc-400 flex-1 truncate">{shareUrl}</div>
              <button
                onClick={copy}
                data-testid="refer-copy"
                className="btn-lime !px-3 !py-1.5 text-xs"
              >
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            {canNativeShare && (
              <button
                onClick={share}
                data-testid="refer-share"
                className="btn-primary w-full text-sm mb-2"
              >
                {shared ? <><Check size={14} /> Shared</> : <><Share2 size={14} /> Share to iMessage, WhatsApp, Email…</>}
              </button>
            )}
            <div className="text-zinc-500 text-xs font-mono">
              Code: <span className="text-amber-400">{ref.referral_code}</span> · Referred so far: <span className="text-amber-400">{ref.referrals_count}</span> · Reward: <span className="text-amber-400">{ref.reward_days} days each</span>
            </div>
          </>
        )}
      </div>

      {/* Learning Mode segmented control */}
      <ModeSwitcher variant="card" />

      {/* Advanced Tradesman toggle */}
      <div data-testid="advanced-card" className={`industrial-card border-t-4 ${user?.is_advanced ? 'border-t-lime-400' : 'border-t-sky-400'} relative overflow-hidden`}>
        <div className="flex items-center gap-2 mb-3">
          <Hammer size={20} className={user?.is_advanced ? 'text-lime-400' : 'text-sky-400'} />
          <div className={`font-mono text-xs uppercase tracking-widest ${user?.is_advanced ? 'text-lime-400' : 'text-sky-400'}`}>
            {user?.is_advanced ? 'Advanced Mode · Active' : 'Advanced Tradesman Mode'}
          </div>
        </div>
        <h2 className="font-display text-3xl text-zinc-100 tracking-tight leading-none mb-3">
          {user?.is_advanced ? 'YOU\u2019RE IN PRO MODE.' : 'UNLOCK THE PRO TOOLKIT'}
        </h2>
        <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
          {user?.is_advanced
            ? 'You have the tradesman toolkit unlocked. Every solved problem now has buddy notes, an AI Repair Report you can email to a customer, and the pro-only prompt style. New pro features roll out here first.'
            : 'Built for tradesmen and serious DIYers. Unlocks: buddy notes on every job · AI Repair Report PDF (great for landlords, insurance, buyers) · deeper prompt style · early access to nameplate parts scanner, regulation checker, and cost-vs-pro tracker.'}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={toggleAdvanced}
            disabled={advBusy}
            data-testid="advanced-toggle"
            className={user?.is_advanced ? 'btn-primary text-sm' : 'btn-lime text-sm'}
          >
            {advBusy ? <Loader2 size={14} className="animate-spin" /> : user?.is_advanced ? (
              <><Check size={14} /> Disable Advanced Mode</>
            ) : (
              <><Zap size={14} /> Enable Advanced Mode</>
            )}
          </button>
          <button
            onClick={() => setAdvTutorial(true)}
            data-testid="advanced-tutorial-btn"
            className="font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 px-3 py-2 border border-zinc-800 hover:border-amber-500"
          >
            How it works
          </button>
        </div>
      </div>

      {/* Change password */}
      <div className="industrial-card border-t-4 border-t-amber-400">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={20} className="text-amber-400" />
          <div className="font-mono text-xs uppercase tracking-widest text-amber-400">Change Password</div>
        </div>
        <form onSubmit={changePassword} className="space-y-3">
          <input
            type="password"
            data-testid="pw-current"
            className="industrial-input !py-2 text-sm"
            placeholder="Current password"
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
          <input
            type="password"
            data-testid="pw-new"
            className="industrial-input !py-2 text-sm"
            placeholder="New password (min 6 chars)"
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            required
          />
          {pwErr && <div className="text-red-400 text-sm font-mono">{pwErr}</div>}
          {pwMsg && <div className="text-lime-400 text-sm font-mono">{pwMsg}</div>}
          <button data-testid="pw-submit" type="submit" disabled={pwBusy} className="btn-primary text-sm">
            {pwBusy ? <Loader2 size={14} className="animate-spin" /> : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Subscription shortcut */}
      <div className="industrial-card !p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-sky-400" />
          <div className="text-sm text-zinc-300">
            Subscription: <span className="font-mono text-zinc-100">{user?.subscription_status}</span>
            {user?.subscription_days_left ? ` · ${user.subscription_days_left} days left` : ''}
          </div>
        </div>
        <Link to="/account/subscribe" className="btn-secondary text-xs">
          Manage <ArrowRight size={12} />
        </Link>
      </div>

      {/* Advanced tutorial modal */}
      {advTutorial && (
        <div
          data-testid="advanced-tutorial"
          className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-up"
          onClick={(e) => { if (e.target === e.currentTarget) setAdvTutorial(false); }}
        >
          <div className="relative bg-zinc-900 border border-zinc-800 max-w-lg w-full p-6 sm:p-8 border-t-4 border-t-lime-400 max-h-[90vh] overflow-y-auto">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-2 flex items-center gap-2">
              <Hammer size={12} /> Advanced Tradesman Tour
            </div>
            <h2 className="font-display text-3xl text-zinc-100 tracking-tight mb-4">HERE'S WHAT'S NEW</h2>
            <ol className="space-y-4 text-sm text-zinc-300">
              <li className="flex gap-3">
                <span className="font-display text-2xl text-lime-400 shrink-0">1</span>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-1">Buddy Notes</div>
                  <div>On every solution page, a Notes panel lets you jot: "torque 15 lb-ft on the manifold", "left off at step 4", or paste a wire-diagram description. Your buddy remembers them next time you open that fix.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl text-lime-400 shrink-0">2</span>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-1">AI Repair Report</div>
                  <div>Tap "Generate Report" on any solved problem — get a print-ready page with the diagnosis, steps performed, parts used, time spent, notes, and maintenance tips. Perfect for landlords, insurance claims, and paying customers.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl text-lime-400 shrink-0">3</span>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-1">Pro-flavored guidance</div>
                  <div>Your buddy uses more trade-specific vocabulary, less hand-holding, and mentions codes/specs by name (NEC section, IPC ref, torque values) instead of consumer-safe simplifications.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl text-lime-400 shrink-0">4</span>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-1">Coming soon (Advanced-only first)</div>
                  <div>Nameplate parts cross-reference · local code checker · cost-vs-pro tracker · predictive maintenance alerts · multi-user job collab.</div>
                </div>
              </li>
            </ol>
            <button
              onClick={() => setAdvTutorial(false)}
              data-testid="advanced-tutorial-close"
              className="btn-primary w-full mt-6 text-sm"
            >
              Got it — let's build stuff
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
